import {AppUrls} from 'shared/config/model/urls';
import {Pages} from 'shared/config/model/pages';

import React from 'react';
import {i18n} from '@lingui/core';
import {t} from '@lingui/core/macro';
import cn from 'classnames';

import BackButton from 'shared/ui/IconButton/BackButton';
import {CheckButton, DeleteButton, EditButton, PauseButton, PlayButton} from 'shared/ui/IconButton/iconButtons';
import MoreActionsDots from 'shared/ui/MoreActions/MoreActionsDots';
import MoreActionsItem from 'shared/ui/MoreActions/MoreActionsItem';
import usePortMonitorActions from 'features/port/model/usePortMonitorActions';
import IPortMonitor from 'features/port/model/PortMonitor/IPortMonitor';
import {getPortMonitorName} from 'features/port/lib/portMonitorHelper';

import {MonitorTagsModal} from 'entities/monitor';

import useUpdatePortMonitorTags from 'features/port/model/useUpdatePortMonitorTags';

import PortMonitorModal from '../../PortMonitorModal';
import PortMonitorDeleteModal from '../../PortMonitorDeleteModal/PortMonitorDeleteModal';
import PortMonitorPauseModal from '../../PortMonitorPauseModal/PortMonitorPauseModal';
import PortMonitorResumeModal from '../../PortMonitorResumeModal/PortMonitorResumeModal';

import styles from './PortMonitorHeader.module.scss';

interface IPortMonitorHeaderProps {
    portMonitor: IPortMonitor,
}

const PortMonitorHeader: React.FC<IPortMonitorHeaderProps> = ({portMonitor}) => {
    const {activeModal, openModal, closeModal, checkNow} = usePortMonitorActions(portMonitor.uuid);

    const name = getPortMonitorName(portMonitor);
    const isActive = portMonitor.status === 'active';

    return (
        <div className={styles.container}>
            <div className={cn(styles.titleBlock, styles.color)}>
                <BackButton to={AppUrls.PORT_MONITORS} text={t`Назад`} className={styles.backButton}/>
                <h3>{i18n._(Pages.PORT_MONITOR_DETAILS)}</h3>
                <div className={styles.name} title={name}>«{name}»</div>
                <EditButton
                    title={t`Редактировать`}
                    iconSize={16}
                    onClick={() => openModal('edit')}
                    className={styles.hideMob}
                />
            </div>
            <div className={styles.actionBlock}>
                <div className={styles.actionButtons}>
                    <CheckButton
                        className={cn(styles.actionButton, styles.check)}
                        title={t`Проверить`}
                        text={t`Проверить`}
                        onClick={checkNow}
                    />
                    {
                        isActive
                            ? <PauseButton
                                className={cn(styles.actionButton, styles.pause)}
                                title={t`На паузу`}
                                iconSize={16}
                                text={t`На паузу`}
                                onClick={() => openModal('pause')}
                            />
                            : <PlayButton
                                className={cn(styles.actionButton, styles.play)}
                                title={t`Включить`}
                                iconSize={16}
                                text={t`Включить`}
                                onClick={() => openModal('resume')}
                            />
                    }
                    <DeleteButton
                        className={styles.deleteButton}
                        title={t`Удалить`}
                        iconSize={16}
                        onClick={() => openModal('delete')}
                    />
                </div>
                <div className={styles.menu}>
                    <MoreActionsDots vertical>
                        <MoreActionsItem label={t`Проверить`} onClick={checkNow}/>
                        {
                            isActive
                                ? <MoreActionsItem
                                    label={t`На паузу`}
                                    onClick={() => openModal('pause')}
                                />
                                : <MoreActionsItem
                                    label={t`Включить`}
                                    onClick={() => openModal('resume')}
                                />
                        }
                        <MoreActionsItem label={t`Редактировать`} onClick={() => openModal('edit')}/>
                        <MoreActionsItem label={t`Теги`} onClick={() => openModal('tags')}/>
                        <MoreActionsItem label={t`Удалить`} onClick={() => openModal('delete')}/>
                    </MoreActionsDots>
                </div>
            </div>

            <PortMonitorModal
                item={portMonitor}
                open={activeModal === 'edit'}
                handleClose={closeModal}
            />

            <PortMonitorPauseModal
                item={portMonitor}
                open={activeModal === 'pause'}
                handleClose={closeModal}
            />

            <PortMonitorResumeModal
                item={portMonitor}
                open={activeModal === 'resume'}
                handleClose={closeModal}
            />

            <PortMonitorDeleteModal
                item={portMonitor}
                open={activeModal === 'delete'}
                handleClose={closeModal}
            />

            <MonitorTagsModal
                uuid={portMonitor.uuid}
                tags={portMonitor.tags}
                useUpdateTags={useUpdatePortMonitorTags}
                open={activeModal === 'tags'}
                handleClose={closeModal}
            />
        </div>
    );
};

export default PortMonitorHeader;
